"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { BLUR_DATA } from "@/app/quiz/_components/blurData";
import { DEFAULT_SIZES, shuffleHeroImages, type HeroImage } from "./heroImages";

const INTERVAL_MS = 5000;

export function HeroImageCarousel({ images }: { images: HeroImage[] }) {
  // Seeded once from the server-shuffled prop — never re-derived from
  // `images` on re-render, or the client-side reshuffle below would be
  // clobbered on every parent render.
  const [order, setOrder] = useState(images);
  const [activeIndex, setActiveIndex] = useState(0);

  // The LCP element is fixed at first paint, not renegotiated every 5
  // seconds — captured once (as state, not a ref: reading a ref's `.current`
  // during render is disallowed by the react-hooks/refs lint rule, even for
  // a value set once at init) so `priority` below always points at the
  // image the server actually preloaded, even after `order` reshuffles and
  // a different image lands at index 0.
  const [initialFirstSrc] = useState(() => images[0]?.src);

  // Mirror `order`/`activeIndex` for synchronous reads inside the interval
  // callback below. The effect that drives the interval has an empty
  // dependency array (it must — it's a single long-lived interval, not one
  // restarted on every reshuffle), so it cannot close over `order` or
  // React's `prev` updater argument directly: that would either permanently
  // pin `order` to the value from the render that created the effect, or
  // force the reshuffle/reveal side effects to live inside the
  // `setActiveIndex` updater — which must stay pure (React may invoke it
  // more than once, e.g. under StrictMode, and a discarded/replayed call
  // must not have already mutated a ref or scheduled other state updates).
  // Kept in sync inline, in the same tick the interval fires, not via a
  // separate useEffect(() => ..., [order]) — that would still lag by one
  // commit relative to the interval callback that just ran.
  const orderRef = useRef(order);
  const activeIndexRef = useRef(activeIndex);

  // Revealed set: tracks which slide `src`s have had their <Image> mounted.
  // Keyed by src (not index) because the order reshuffles at each loop
  // boundary — an index-keyed set would mark the wrong slides as loaded the
  // moment that happens. Initialised to the first two so slide 0 paints
  // immediately (priority) and slide 1 preloads ahead of the first
  // crossfade. Slides are never un-mounted once revealed so re-showing on
  // loop is instant and fade-outs keep working.
  const [revealed, setRevealed] = useState<ReadonlySet<string>>(
    () => new Set(order.slice(0, 2).map((img) => img.src)),
  );

  useEffect(() => {
    const id = setInterval(() => {
      const currentOrder = orderRef.current;
      const len = currentOrder.length;
      if (len === 0) return;

      const next = (activeIndexRef.current + 1) % len;
      activeIndexRef.current = next;

      let nextOrder = currentOrder;
      if (next === 0) {
        // Loop seam: reshuffle for the next cycle, constrained so the new
        // first image never repeats the one just shown last.
        nextOrder = shuffleHeroImages(
          currentOrder,
          Math.random,
          currentOrder[len - 1]?.src,
        );
        orderRef.current = nextOrder;
        setOrder(nextOrder);
      }

      const incoming = nextOrder[next]!;
      const preload = nextOrder[(next + 1) % len];
      setRevealed((r) => {
        if (r.has(incoming.src) && (!preload || r.has(preload.src))) {
          return r;
        }
        const copy = new Set(r);
        copy.add(incoming.src);
        if (preload) copy.add(preload.src);
        return copy;
      });

      setActiveIndex(next);
    }, INTERVAL_MS);

    return () => clearInterval(id);
  }, []);

  return (
    <div
      className="relative h-full w-full overflow-hidden rounded-[var(--radius-lg)]"
      aria-hidden="true"
    >
      {order.map((img, i) => (
        <div
          key={img.src}
          className={[
            "absolute inset-0 transition-opacity duration-[1200ms]",
            i === activeIndex ? "opacity-100" : "opacity-0",
          ].join(" ")}
        >
          {revealed.has(img.src) && (
            <Image
              src={img.src}
              alt={img.alt}
              fill
              priority={img.src === initialFirstSrc}
              sizes={img.sizes ?? DEFAULT_SIZES}
              className="object-cover"
              {...(img.objectPosition
                ? { style: { objectPosition: img.objectPosition } }
                : {})}
              {...(BLUR_DATA[img.src]
                ? { placeholder: "blur", blurDataURL: BLUR_DATA[img.src] }
                : {})}
            />
          )}
        </div>
      ))}
    </div>
  );
}
